import React, { useState } from 'react';
import {
  Pane,
  FileUploader,
  Button,
  Select,
  toaster,
  Heading,
  Card,
  Text,
  Badge,
  Spinner,
  Textarea,
  Tablist,
  Tab,
  Label,
  Checkbox
} from 'evergreen-ui';
import { useProject } from '../context/ProjectContext';
import api from '../services/api';

const PASTE_SUPPORTED_TYPES = ['domains', 'ips', 'extract'];
const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB per chunk
const CHUNKED_THRESHOLD = 50 * 1024 * 1024; // Use chunked upload for files > 50MB

const FileUpload = () => {
  const { currentProject } = useProject();
  const [files, setFiles] = useState([]);
  const [fileType, setFileType] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [inputMode, setInputMode] = useState('file');
  const [pasteContent, setPasteContent] = useState('');
  const [inScopeOnly, setInScopeOnly] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');

  const handleSelect = files => {
    setFiles([...files]);
  };

  const showSummary = (summary) => {
    const badges = [];
    if (summary.domains) {
      badges.push(<Badge key="domains" marginRight={8}>Domains: {summary.domains}</Badge>);
    }
    if (summary.hosts) {
      badges.push(<Badge key="hosts" marginRight={8}>Hosts: {summary.hosts}</Badge>);
    }
    if (summary.ports) {
      badges.push(<Badge key="ports" marginRight={8}>Ports: {summary.ports}</Badge>);
    }
    if (summary.findings) {
      badges.push(<Badge key="findings" marginRight={8}>Findings: {summary.findings}</Badge>);
    }
    if (summary.urls) {
      badges.push(<Badge key="urls" marginRight={8}>URLs: {summary.urls}</Badge>);
    }
    if (summary.cidrs) {
      badges.push(<Badge key="cidrs" marginRight={8}>CIDRs: {summary.cidrs}</Badge>);
    }
    if (summary.out_of_scope_skipped) {
      badges.push(<Badge key="oos" color="yellow" marginRight={8}>Out-of-scope skipped: {summary.out_of_scope_skipped}</Badge>);
    }
    if (summary.duplicates_found) {
      badges.push(<Badge key="dedup" color="orange" marginRight={8}>Duplicates: {summary.duplicates_found}</Badge>);
    }
    if (summary.coverage_nodes) {
      badges.push(<Badge key="coverage" color="green" marginRight={8}>Coverage Nodes: {summary.coverage_nodes}</Badge>);
    }
    if (badges.length > 0) {
      toaster.notify(
        <Pane>
          <Text marginBottom={4}>Processed:</Text>
          <Pane>{badges}</Pane>
        </Pane>,
        { duration: 10 }
      );
    }
  };

  const uploadChunkWithRetry = async (url, formData, maxRetries = 5) => {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await api.post(url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        });
      } catch (err) {
        if (attempt === maxRetries) throw err;
        const delay = Math.min(2000 * Math.pow(2, attempt), 30000);
        setUploadStatus(`Chunk failed, retrying in ${Math.round(delay / 1000)}s (attempt ${attempt + 2}/${maxRetries + 1})...`);
        await new Promise(r => setTimeout(r, delay));
      }
    }
  };

  const uploadChunked = async (file) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const projectId = currentProject.id;

    // 1. Init upload session
    setUploadStatus(`Initializing upload (${totalChunks} chunks)...`);
    const initRes = await api.post(`/projects/${projectId}/upload/init`, {
      file_name: file.name,
      file_type: fileType,
      total_chunks: totalChunks,
      file_size: file.size,
      in_scope_only: inScopeOnly,
    });
    const { upload_id } = initRes.data;

    // 2. Upload chunks sequentially with retry
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);

      const chunkForm = new FormData();
      chunkForm.append('file', chunk, `chunk_${i}`);
      chunkForm.append('upload_id', upload_id);
      chunkForm.append('chunk_index', i);

      await uploadChunkWithRetry(`/projects/${projectId}/upload/chunk`, chunkForm);

      const pct = Math.round(((i + 1) / totalChunks) * 100);
      setUploadProgress(pct);
      setUploadStatus(`Uploading chunk ${i + 1}/${totalChunks}...`);
    }

    // 3. Complete - assemble and process
    setUploadStatus('Processing file on server...');
    const completeRes = await api.post(`/projects/${projectId}/upload/complete`, {
      upload_id,
    }, { timeout: 600000 }); // 10 min for server-side processing

    return completeRes;
  };

  const handleUpload = async () => {
    if (!currentProject?.id) {
      toaster.danger('Please select a project first');
      return;
    }
    if (!fileType) {
      toaster.warning('Please select a file type');
      return;
    }
    if (files.length === 0) {
      toaster.warning('Please select a file to upload');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setUploadStatus('');

    const file = files[0];

    try {
      let response;

      if (file.size > CHUNKED_THRESHOLD) {
        // Large file: use chunked upload
        response = await uploadChunked(file);
      } else {
        // Small file: use regular single-request upload
        const formData = new FormData();
        formData.append('file', file);
        formData.append('file_type', fileType);
        formData.append('in_scope_only', inScopeOnly);

        response = await api.post(
          `/projects/${currentProject.id}/upload`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 600000,
            onUploadProgress: (e) => {
              if (e.total) {
                setUploadProgress(Math.round((e.loaded / e.total) * 100));
              }
            },
          }
        );
      }

      toaster.success('File uploaded and processed successfully');
      setFiles([]);
      setFileType('');
      if (response.data.summary) {
        showSummary(response.data.summary);
      }
    } catch (error) {
      toaster.danger(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setUploadStatus('');
    }
  };

  const handlePasteSubmit = async () => {
    if (!currentProject?.id) {
      toaster.danger('Please select a project first');
      return;
    }
    if (!fileType) {
      toaster.warning('Please select a file type');
      return;
    }
    if (!pasteContent.trim()) {
      toaster.warning('Please paste some content to parse');
      return;
    }

    setIsUploading(true);
    try {
      const response = await api.post(
        `/projects/${currentProject.id}/upload/text`,
        { content: pasteContent, file_type: fileType, in_scope_only: inScopeOnly }
      );
      toaster.success('Text parsed and processed successfully');
      setPasteContent('');
      setFileType('');
      if (response.data.summary) {
        showSummary(response.data.summary);
      }
    } catch (error) {
      toaster.danger(error.response?.data?.detail || 'Failed to parse text');
    } finally {
      setIsUploading(false);
    }
  };

  if (!currentProject) {
    return (
      <Card background="tint1" padding={16} marginBottom={16}>
        <Heading size={500} marginBottom={16}>Upload Data</Heading>
        <Text color="muted" textAlign="center">
          Please select a project first to upload data.
        </Text>
      </Card>
    );
  }

  const pasteSupported = PASTE_SUPPORTED_TYPES.includes(fileType);

  return (
    <Card background="tint1" padding={16} marginBottom={16}>
      <Heading size={500} marginBottom={16}>Upload Data</Heading>

      <Pane marginBottom={16}>
        <Select
          width="100%"
          value={fileType}
          onChange={e => {
            const newType = e.target.value;
            setFileType(newType);
            // Switch back to file mode if paste not supported for this type
            if (!PASTE_SUPPORTED_TYPES.includes(newType) && inputMode === 'paste') {
              setInputMode('file');
            }
          }}
        >
          <option value="">Select file type...</option>
          <optgroup label="Basic Lists">
            <option value="domains">Domain List (txt)</option>
            <option value="ips">IP List (txt)</option>
            <option value="extract">Extract IPs, domains, URLs and CIDRs (any text)</option>
          </optgroup>
          <optgroup label="Scanner Results">
            <option value="nmap">Nmap XML</option>
            <option value="nessus">Nessus XML</option>
            <option value="burp">Burp Suite (XML/JSON export)</option>
            <option value="acunetix">Acunetix XML</option>
            <option value="nuclei">Nuclei JSON</option>
            <option value="ffuf">FFUF JSON</option>
          </optgroup>
        </Select>
      </Pane>

      {fileType && (
        <Pane marginBottom={16}>
          <Checkbox
            label="Process only in-scope items"
            checked={inScopeOnly}
            onChange={e => setInScopeOnly(e.target.checked)}
          />
        </Pane>
      )}

      {fileType && pasteSupported && (
        <Pane marginBottom={16}>
          <Tablist>
            {[
              { label: 'Upload File', value: 'file' },
              { label: 'Paste Text', value: 'paste' }
            ].map(tab => (
              <Tab
                key={tab.value}
                isSelected={inputMode === tab.value}
                onSelect={() => setInputMode(tab.value)}
              >
                {tab.label}
              </Tab>
            ))}
          </Tablist>
        </Pane>
      )}

      {inputMode === 'file' ? (
        <>
          <Pane marginBottom={16}>
            <FileUploader
              label="Select file"
              description={fileType ? `Upload ${fileType} file` : 'Please select file type first'}
              onChange={handleSelect}
              maxSizeInBytes={1024 * 1024 * 1024}
              values={files}
              disabled={!fileType || isUploading}
            />
          </Pane>
          <Button
            appearance="primary"
            onClick={handleUpload}
            disabled={!fileType || files.length === 0 || isUploading}
            isLoading={isUploading}
          >
            Upload and Process
          </Button>
          {isUploading && uploadProgress > 0 && (
            <Pane marginTop={12}>
              <Pane display="flex" alignItems="center" gap={8} marginBottom={4}>
                <Text size={300} color="muted">
                  {uploadStatus || (uploadProgress < 100 ? `Uploading... ${uploadProgress}%` : 'Processing file on server...')}
                </Text>
              </Pane>
              <Pane height={6} background="#edeff5" borderRadius={3} overflow="hidden">
                <Pane
                  height="100%"
                  borderRadius={3}
                  width={`${uploadProgress}%`}
                  style={{
                    background: uploadProgress < 100
                      ? 'linear-gradient(90deg, #3366FF, #5C85FF)'
                      : 'linear-gradient(90deg, #52BD94, #429777)',
                    transition: 'width 0.3s ease',
                  }}
                />
              </Pane>
            </Pane>
          )}
        </>
      ) : (
        <>
          <Pane marginBottom={16}>
            <Label htmlFor="paste-area" marginBottom={4} display="block">
              Paste content to extract data from
            </Label>
            <Textarea
              id="paste-area"
              placeholder={
                fileType === 'domains'
                  ? 'Paste domains, one per line or comma-separated...\nexample.com\ntest.org, sub.example.com'
                  : fileType === 'ips'
                  ? 'Paste IP addresses, one per line or comma-separated...\n192.168.1.1\n10.0.0.1, 172.16.0.1'
                  : 'Paste any text containing URLs, domains, IPs, CIDRs...\nhttps://example.com/path\n192.168.1.0/24\ntest.example.org\n10.0.0.1'
              }
              value={pasteContent}
              onChange={e => setPasteContent(e.target.value)}
              disabled={!fileType || isUploading}
              width="100%"
              height={200}
              fontFamily="mono"
              fontSize={13}
            />
            {pasteContent.trim() && (
              <Text size={300} color="muted" marginTop={4}>
                {pasteContent.trim().split(/\n/).length} lines pasted
              </Text>
            )}
          </Pane>
          <Button
            appearance="primary"
            onClick={handlePasteSubmit}
            disabled={!fileType || !pasteContent.trim() || isUploading}
            isLoading={isUploading}
          >
            Parse and Process
          </Button>
        </>
      )}
    </Card>
  );
};

export default FileUpload;
